import type { ColumnsType } from "antd/es/table";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { normalizeExternalUrl } from "pages/grid/utils/gridHelpers";
import useTranslation from "utils/hooks/useTranslation";

type TableRecord = Record<string, any>;

type UseGridColumnsParams = {
  tableWidget: any;
  rows: TableRecord[];
};

const linkButtonStyle: React.CSSProperties = {
  background: "none",
  border: "none",
  padding: 0,
  color: "RoyalBlue",
  cursor: "pointer",
  textDecoration: "underline",
  font: "inherit",
  textAlign: "left",
};

function formatGridDateValue(fieldKey: string, value: unknown): string {
  if (value == null) return "";

  const toSafeString = (v: unknown) => {
    if (typeof v === "string") return v;
    if (typeof v === "number" || typeof v === "boolean") return String(v);
    if (v instanceof Date) return v.toISOString();
    try {
      return JSON.stringify(v);
    } catch {
      return "";
    }
  };

  const key = String(fieldKey).toLowerCase();
  const isDateField = key === "start_date" || key === "end_date" || key.endsWith("_date");
  if (!isDateField) return toSafeString(value);

  const raw = toSafeString(value).trim();
  if (!raw) return "";

  // Keep date rendering deterministic and aligned with legacy grid: YYYY/MM/DD.
  const datePart = raw.includes("T") ? raw.split("T")[0] : raw;
  if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
    return datePart.replace(/-/g, "/");
  }

  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    const y = parsed.getUTCFullYear();
    const m = String(parsed.getUTCMonth() + 1).padStart(2, "0");
    const d = String(parsed.getUTCDate()).padStart(2, "0");
    return `${y}/${m}/${d}`;
  }

  return raw;
}

export function useGridColumns({ tableWidget, rows }: UseGridColumnsParams): ColumnsType<TableRecord> {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleLinkedCellClick = useCallback((fieldConfig: any, row: TableRecord, value: unknown) => {
    const linkSourceValue = fieldConfig?.linkOwnField ? row?.[fieldConfig.linkOwnField] : value;
    const externalUrl = normalizeExternalUrl(linkSourceValue);
    if (externalUrl) {
      globalThis.open(externalUrl, "_blank", "noopener,noreferrer");
      return;
    }

    const linkValue = String(fieldConfig?.linkValue ?? "").trim();
    const linkField = String(fieldConfig?.linkField ?? "").trim();
    const finalParam = String(linkSourceValue ?? "").trim();
    if (!linkValue || !linkField || !finalParam) return;

    navigate(
      `/layout/${encodeURIComponent(linkValue)}?${encodeURIComponent(linkField)}=${encodeURIComponent(finalParam)}`
    );
  }, [navigate]);

  return useMemo(() => {
    if (tableWidget?.fields?.length) {
      return tableWidget.fields
        .filter((f: any) => f.display !== false)
        .map((f: any) => {
          const isUrlField = String(f.field ?? "").toLowerCase() === "url";
          const isLinkField = Boolean(f.link) || isUrlField;

          return {
            key: f.field,
            title: t(f.field),
            dataIndex: f.field,
            sorter: true,
            render: (value: unknown, row: TableRecord) => {
              const displayValue = formatGridDateValue(String(f.field ?? ""), value);
              if (!isLinkField || !displayValue.trim()) return displayValue;

              const linkSourceValue = f.linkOwnField ? row?.[f.linkOwnField] : value;
              const isClickable =
                normalizeExternalUrl(linkSourceValue) != null ||
                (Boolean(f.linkValue) && Boolean(f.linkField));
              if (!isClickable) return displayValue;

              return (
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleLinkedCellClick(f, row, value);
                  }}
                  style={linkButtonStyle}
                >
                  {displayValue}
                </button>
              );
            },
          };
        });
    }

    if (!rows.length) return [];
    const keys = Object.keys(rows[0]).filter((k) => k !== "key");

    return keys.map((key) => {
      return {
        key,
        title: t(key),
        dataIndex: key,
        sorter: true,
        render: (value: unknown) => {
        const displayValue = formatGridDateValue(String(key ?? ""), value);
        if (String(key).toLowerCase() !== "url" || !displayValue.trim()) return displayValue;

        const externalUrl = normalizeExternalUrl(value);
        if (!externalUrl) return displayValue;

        return (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              globalThis.open(externalUrl, "_blank", "noopener,noreferrer");
            }}
            style={linkButtonStyle}
          >
            {displayValue}
          </button>
        );
        },
      };
    });
  }, [handleLinkedCellClick, rows, tableWidget, t]);
}
